/**
 * Registro giornate: atleti × giorni. Ogni cella riassume la dimensione
 * «programmazione» (assegnato / nessuna) e quella «esito» (completo, parziale,
 * non registrato), con un marcatore per l'attivita' extra. La presenza non e'
 * verificata — la piattaforma non ha check-in — e la legenda lo dice.
 * Il colore e' sempre doppiato dall'icona e dal testo del tooltip.
 */
import { ChevronRight, Plus } from "lucide-react";
import {
	dayStatusMeta,
	formatShortDay,
	weekdayShort,
} from "../../lib/monitoringUtils";
import { formatFullName } from "../../lib/utils";
import { HintTooltip } from "../programBuilder/HintTooltip";
import { STATUS_ICON, useIsCompact } from "./primitives";

const ICON_CLASS = {
	complete: "text-volt",
	partial: "text-amber-600 dark:text-amber-400",
	none: "text-blaze",
	upcoming: "text-muted-foreground",
	rest: "text-muted-foreground/40",
};

function cellLabel(cell, date, t, language) {
	const parts = [formatShortDay(date, language), t(dayStatusMeta(cell?.status).labelKey)];
	if (cell?.assigned > 1) parts.push(`${cell.assigned} ${t("monAssignedShort")}`);
	else if (cell?.sessions > 1) parts.push(`${cell.sessions} ${t("monLegendSessions")}`);
	if (cell?.extra) parts.push(t("monLegendExtra"));
	else if (cell?.diary) parts.push(t("monLegendDiary"));
	parts.push(cell?.presence === "declared" ? t("monPresenceDeclared") : t("monPresenceUnknown"));
	return parts.join(" · ");
}

/** Icona, colore e marcatore di una cella: gli stessi nel registro largo e nelle schede del telefono. */
function cellVisual(cell) {
	const status = cell?.status || "rest";
	const extraOnly = status === "rest" && Boolean(cell?.extra || cell?.diary);
	return {
		Icon: extraOnly ? Plus : STATUS_ICON[status] || STATUS_ICON.rest,
		iconClass: extraOnly ? "text-sky-600 dark:text-sky-400" : ICON_CLASS[status],
		extraDot: !extraOnly && status !== "rest" && Boolean(cell?.extra || cell?.diary),
	};
}

function ExtraDot() {
	return (
		<span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-sky-500 ring-2 ring-card" aria-hidden="true" />
	);
}

function Cell({ cell, date, t, language, onClick }) {
	const { Icon, iconClass, extraDot } = cellVisual(cell);
	const label = cellLabel(cell, date, t, language);
	return (
		<HintTooltip label={label}>
			<button
				type="button"
				onClick={onClick}
				aria-label={label}
				className="relative inline-flex h-8 w-8 items-center justify-center rounded-sm hover:bg-muted/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-volt/50"
			>
				<Icon className={`w-4 h-4 ${iconClass}`} aria-hidden="true" />
				{extraDot ? <ExtraDot /> : null}
			</button>
		</HintTooltip>
	);
}

/**
 * Un giorno nella scheda del telefono: iniziale e numero del giorno sopra
 * l'icona, perche' col dito il tooltip non si apre. 44px d'altezza, la misura
 * minima per toccare senza sbagliare.
 */
function DayTile({ cell, date, today, weekend, t, language, onClick }) {
	const { Icon, iconClass, extraDot } = cellVisual(cell);
	return (
		<button
			type="button"
			onClick={onClick}
			aria-label={cellLabel(cell, date, t, language)}
			className={`relative flex h-11 flex-col items-center justify-center rounded-sm border focus:outline-none focus-visible:ring-2 focus-visible:ring-volt/50 ${
				date === today ? "border-volt/60" : "border-transparent"
			} ${weekend ? "bg-muted/30" : "bg-muted/10"}`}
		>
			<span className="text-[9px] uppercase leading-none text-muted-foreground">{weekdayShort(date, language)}</span>
			<span className="text-[11px] tabular-nums leading-tight">{date.slice(8, 10)}</span>
			<Icon className={`w-3.5 h-3.5 ${iconClass}`} aria-hidden="true" />
			{extraDot ? <ExtraDot /> : null}
		</button>
	);
}

/** Il registro sul telefono: una scheda per atleta, i giorni a righe da sette. */
function AttendanceCards({ rows, dates, today, isWeekend, t, language, onSelectDay }) {
	const weeks = [];
	for (let index = 0; index < dates.length; index += 7) weeks.push(dates.slice(index, index + 7));
	return (
		<ul className="space-y-2" data-testid="mon-attendance-cards">
			{rows.map((row) => (
				<li
					key={row.athlete_id}
					className="rounded-sm border border-border bg-card px-3 py-2.5 space-y-2"
					data-testid={`mon-attendance-card-${row.athlete_id}`}
				>
					<button
						type="button"
						onClick={() => onSelectDay(row.athlete_id)}
						className="w-full flex items-center justify-between gap-2 text-left"
					>
						<span className="min-w-0">
							<span className="block font-medium truncate">{formatFullName(row)}</span>
							{row.email ? (
								<span className="block text-[11px] text-muted-foreground truncate">{row.email}</span>
							) : null}
						</span>
						<ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden="true" />
					</button>
					{weeks.map((week) => (
						<div key={week[0]} className="grid grid-cols-7 gap-1">
							{week.map((date) => (
								<DayTile
									key={date}
									cell={row.cells?.[date]}
									date={date}
									today={today}
									weekend={isWeekend(date)}
									t={t}
									language={language}
									onClick={() => onSelectDay(row.athlete_id, date)}
								/>
							))}
						</div>
					))}
				</li>
			))}
		</ul>
	);
}

function LegendItem({ icon: Icon, cls, label }) {
	return (
		<span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
			<Icon className={`w-3.5 h-3.5 ${cls}`} aria-hidden="true" />
			{label}
		</span>
	);
}

export function AttendanceGrid({ rows, dates, today, t, language, onSelectDay }) {
	const compact = useIsCompact();
	if (!rows.length) {
		return (
			<div className="rounded-sm border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
				{t("monEmpty")}
			</div>
		);
	}
	const isWeekend = (iso) => {
		const day = new Date(`${iso}T12:00:00Z`).getUTCDay();
		return day === 0 || day === 6;
	};
	return (
		<div className="space-y-3">
			{compact ? (
				<AttendanceCards
					rows={rows}
					dates={dates}
					today={today}
					isWeekend={isWeekend}
					t={t}
					language={language}
					onSelectDay={onSelectDay}
				/>
			) : (
				<div className="rounded-sm border border-border bg-card overflow-x-auto">
					<table className="text-sm border-separate border-spacing-0">
						<thead>
							<tr>
								<th className="sticky left-0 z-10 bg-card px-3 py-2 text-left text-[10px] uppercase tracking-widest text-muted-foreground font-medium min-w-[180px] border-b border-border">
									{t("monColAthlete")}
								</th>
								{dates.map((date) => (
									<th
										key={date}
										className={`px-0.5 py-1.5 text-center font-normal border-b border-border ${
											date === today ? "border-t-2 border-t-volt" : ""
										} ${isWeekend(date) ? "bg-muted/20" : ""}`}
									>
										<div className="text-[10px] uppercase text-muted-foreground">
											{weekdayShort(date, language)}
										</div>
										<div className="text-xs tabular-nums">{date.slice(8, 10)}</div>
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{rows.map((row) => (
								<tr key={row.athlete_id} className="hover:bg-muted/20">
									<th
										scope="row"
										className="sticky left-0 z-10 bg-card px-3 py-1 text-left font-medium border-b border-border/60 whitespace-nowrap"
									>
										<div className="truncate max-w-[200px]">{formatFullName(row)}</div>
										{row.email ? (
											<div className="text-[11px] text-muted-foreground font-normal truncate max-w-[200px]">
												{row.email}
											</div>
										) : null}
									</th>
									{dates.map((date) => (
										<td
											key={date}
											className={`px-0.5 py-1 text-center border-b border-border/60 ${
												isWeekend(date) ? "bg-muted/20" : ""
											}`}
										>
											<Cell
												cell={row.cells?.[date]}
												date={date}
												t={t}
												language={language}
												onClick={() => onSelectDay(row.athlete_id, date)}
											/>
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			<div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-1">
				<span className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("monLegend")}</span>
				<LegendItem icon={STATUS_ICON.complete} cls={ICON_CLASS.complete} label={t("monStatusComplete")} />
				<LegendItem icon={STATUS_ICON.partial} cls={ICON_CLASS.partial} label={t("monStatusPartial")} />
				<LegendItem icon={STATUS_ICON.none} cls={ICON_CLASS.none} label={t("monStatusNone")} />
				<LegendItem icon={STATUS_ICON.upcoming} cls={ICON_CLASS.upcoming} label={t("monStatusUpcoming")} />
				<LegendItem icon={STATUS_ICON.rest} cls={ICON_CLASS.rest} label={t("monStatusRest")} />
				<LegendItem icon={Plus} cls="text-sky-600 dark:text-sky-400" label={t("monLegendExtra")} />
			</div>
			{compact ? <p className="text-xs text-muted-foreground px-1">{t("monAttendanceTapHint")}</p> : null}
			<p className="text-xs text-muted-foreground px-1">{t("monPresenceNote")}</p>
		</div>
	);
}
